import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import HealthMetricCard from './HealthMetricCard'
import { useMetricCardCopy } from '../../hooks/useMetricCardCopy'
import { KILO } from '../../constants/KILO_THEME'
import { getMetricTrend, type CoachHealthSummary } from '../../lib/coachHealthTypes'
import { supabase } from '../../lib/supabaseClient'

type CoachBodyMetricsPanelProps = {
  summary: CoachHealthSummary | null
  loading: boolean
  connecting: boolean
  onConnecting: () => void
  onRefresh: (sync?: boolean) => void
  embedded?: boolean
}

function DeviceConnectInline({ onError }: { onError: (message: string) => void }) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  async function connect() {
    setLoading(true)
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('vital-connect', {
        body: { redirect_url: `${window.location.origin}/app?tab=coach&connected=true` },
      })
      if (invokeError) throw invokeError
      if (data?.error) throw new Error(String(data.error))
      if (!data?.link_web_url) throw new Error(t('coach.metrics.missingLink'))
      window.location.href = data.link_web_url as string
    } catch (err) {
      onError(err instanceof Error ? err.message : t('coach.metrics.connectFailed'))
      setLoading(false)
    }
  }

  return (
    <div className="ml-auto flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={() => void connect()}
        disabled={loading}
        className="text-xs text-kilo-soft hover:text-kilo-text disabled:opacity-50"
      >
        {loading ? '…' : t('coach.metrics.addDevice')}
      </button>
    </div>
  )
}

const CoachBodyMetricsPanel = ({
  summary,
  loading,
  connecting,
  onConnecting: _onConnecting,
  onRefresh,
  embedded = false,
}: CoachBodyMetricsPanelProps) => {
  const { t, i18n } = useTranslation()
  const { labelFor, catalogue } = useMetricCardCopy()
  const [customising, setCustomising] = useState(false)
  const [draftVisible, setDraftVisible] = useState<string[]>(summary?.visible_metrics ?? [])
  const [saving, setSaving] = useState(false)
  const [inlineError, setInlineError] = useState<string | null>(null)

  useEffect(() => {
    if (summary?.visible_metrics) {
      setDraftVisible(summary.visible_metrics)
    }
  }, [summary?.visible_metrics])

  if (loading && !summary && !connecting) {
    return (
      <div className={embedded ? '' : 'space-y-3 border-t border-kilo-border pt-4'}>
        {!embedded ? <p className={KILO.meta}>{t('coach.metrics.title')}</p> : null}
        <div className="flex h-24 items-center justify-center">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-kilo-line border-t-kilo-accent" />
        </div>
      </div>
    )
  }

  if (!summary?.has_device_connected) {
    return null
  }

  const today = summary.today
  const weekAvg = summary.week_averages
  const visibleMetrics = summary.visible_metrics
  const waitingForData = !summary.has_metric_data

  async function savePreferences() {
    setSaving(true)
    const { error } = await supabase.functions.invoke('update-coach-preferences', {
      body: { visible_metrics: draftVisible },
    })
    setSaving(false)
    if (error) {
      console.error('Failed to save coach preferences:', error)
      return
    }
    setCustomising(false)
    onRefresh(false)
  }

  const formatWeekAvg = (id: string, value: unknown, unit?: string) => {
    if (value == null) return t('coach.metrics.weekAvg', { value: '—', unit: unit ?? '' })
    if (id === 'active_calories') {
      return t('coach.metrics.weekAvgKcal', { value })
    }
    if (id === 'steps') {
      return t('coach.metrics.weekAvgSteps', { value: Number(value).toLocaleString(i18n.language) })
    }
    return t('coach.metrics.weekAvg', { value, unit: unit ?? '' })
  }

  return (
    <div className={embedded ? 'space-y-3' : 'space-y-3 border-t border-kilo-border pt-4'}>
      <div className="flex items-center justify-between">
        <p className={KILO.meta}>{embedded ? t('coach.metrics.wearableMetrics') : t('coach.metrics.title')}</p>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void onRefresh(true)}
            className="text-xs text-kilo-soft transition-colors hover:text-kilo-text"
          >
            {t('coach.metrics.refresh')}
          </button>
          <button
            type="button"
            onClick={() => {
              setDraftVisible(visibleMetrics)
              setCustomising(!customising)
            }}
            className="text-xs text-kilo-muted transition-colors hover:text-kilo-text"
          >
            {customising ? t('coach.metrics.cancel') : t('coach.metrics.customise')}
          </button>
        </div>
      </div>

      {waitingForData ? (
        <div className="rounded-2xl border border-kilo-border bg-kilo-card px-4 py-3 text-center">
          <p className={`text-sm ${KILO.text.primary}`}>{t('coach.metrics.syncPendingTitle')}</p>
          <p className="mt-1 text-xs text-kilo-soft">{t('coach.metrics.syncPendingHint')}</p>
        </div>
      ) : null}

      {today.date && String(today.date) !== new Date().toISOString().split('T')[0] ? (
        <p className="text-center text-[10px] text-kilo-soft">
          {t('coach.metrics.latestDataFrom', { date: String(today.date) })}
        </p>
      ) : null}

      {customising ? (
        <div className="space-y-3 rounded-2xl border border-kilo-line bg-kilo-card p-4">
          <p className="text-xs text-kilo-muted">{t('coach.metrics.customiseHint')}</p>
          <div className="grid grid-cols-2 gap-2">
            {catalogue.map((card) => {
              const isVisible = draftVisible.includes(card.id)
              const isLocked = card.coach_uses
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => {
                    if (isLocked) return
                    setDraftVisible((prev) =>
                      isVisible ? prev.filter((id) => id !== card.id) : [...prev, card.id],
                    )
                  }}
                  className={`flex items-center justify-between rounded-xl px-3 py-2 text-xs ${
                    isVisible
                      ? 'border border-kilo-accent/30 bg-kilo-accent/10 text-kilo-text'
                      : 'border border-kilo-line bg-kilo-nested text-kilo-muted'
                  } ${isLocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-kilo-soft'}`}
                >
                  <span>{card.label}</span>
                  {isLocked ? <span className="text-[9px] text-kilo-accent/60">{t('coach.metrics.locked')}</span> : null}
                  {!isLocked && isVisible ? <span className="text-kilo-accent">✓</span> : null}
                </button>
              )
            })}
          </div>
          <button
            type="button"
            onClick={() => void savePreferences()}
            disabled={saving}
            className="w-full rounded-xl bg-kilo-accent py-2 text-sm font-semibold text-black disabled:opacity-50"
          >
            {saving ? t('coach.metrics.saving') : t('coach.metrics.saveLayout')}
          </button>
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3">
        {visibleMetrics.includes('hrv') ? (
          <HealthMetricCard
            label={labelFor('hrv')}
            value={today.hrv as number | null}
            unit="ms"
            trend={getMetricTrend(today.hrv as number | null, weekAvg.hrv as number | null)}
            trendGood="up"
            subtitle={formatWeekAvg('hrv', weekAvg.hrv, 'ms')}
          />
        ) : null}

        {visibleMetrics.includes('resting_heart_rate') ? (
          <HealthMetricCard
            label={labelFor('resting_heart_rate')}
            value={today.resting_heart_rate as number | null}
            unit="bpm"
            trend={getMetricTrend(
              today.resting_heart_rate as number | null,
              weekAvg.resting_heart_rate as number | null,
            )}
            trendGood="down"
          />
        ) : null}

        {visibleMetrics.includes('sleep_duration') ? (
          <HealthMetricCard
            label={labelFor('sleep_duration')}
            value={
              today.sleep_duration_minutes
                ? Math.round(((today.sleep_duration_minutes as number) / 60) * 10) / 10
                : null
            }
            unit="hrs"
            trend={getMetricTrend(
              today.sleep_duration_minutes as number | null,
              weekAvg.sleep_duration_minutes as number | null,
            )}
            trendGood="up"
          />
        ) : null}

        {visibleMetrics.includes('recovery_score') ? (
          <HealthMetricCard
            label={labelFor('recovery_score')}
            value={today.recovery_score as number | null}
            unit="/100"
            trendGood="up"
          />
        ) : null}

        {visibleMetrics.includes('active_calories') ? (
          <HealthMetricCard
            label={labelFor('active_calories')}
            value={today.active_calories as number | null}
            unit="kcal"
            trendGood="up"
            subtitle={formatWeekAvg('active_calories', weekAvg.active_calories)}
          />
        ) : null}

        {visibleMetrics.includes('steps') ? (
          <HealthMetricCard
            label={labelFor('steps')}
            value={today.steps as number | null}
            trendGood="up"
            subtitle={formatWeekAvg('steps', weekAvg.steps)}
          />
        ) : null}

        {visibleMetrics.includes('vo2_max') ? (
          <HealthMetricCard
            label={labelFor('vo2_max')}
            value={today.vo2_max as number | null}
            unit="ml/kg"
            trendGood="up"
          />
        ) : null}

        {visibleMetrics.includes('weight') ? (
          <HealthMetricCard label={labelFor('weight')} value={today.weight_kg as number | null} unit="kg" />
        ) : null}

        {visibleMetrics.includes('body_fat') ? (
          <HealthMetricCard
            label={labelFor('body_fat')}
            value={today.body_fat_pct as number | null}
            unit="%"
            trendGood="down"
          />
        ) : null}

        {visibleMetrics.includes('stress_level') ? (
          <HealthMetricCard
            label={labelFor('stress_level')}
            value={today.avg_stress_level as number | null}
            unit="/100"
            trendGood="down"
          />
        ) : null}

        {visibleMetrics.includes('workout_strain') ? (
          <HealthMetricCard
            label={labelFor('workout_strain')}
            value={today.workout_strain_score as number | null}
            trendGood="down"
          />
        ) : null}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <div className="h-1.5 w-1.5 rounded-full bg-kilo-accent" />
        {inlineError ? (
          <p className="text-xs text-red-400">{inlineError}</p>
        ) : (
          <p className="text-xs text-kilo-soft">
            {t('coach.metrics.deviceConnected', {
              provider: summary.connected_providers.join(', ') || t('coach.metrics.deviceFallback'),
            })}
          </p>
        )}
        <DeviceConnectInline onError={setInlineError} />
      </div>
    </div>
  )
}

export default CoachBodyMetricsPanel
